import React, { useState, useEffect, useCallback } from 'react';

const TOUR_KEY = 'indiepress_editor_tour_seen';

const STEPS = [
  {
    selector: '.notion-sidebar',
    title: 'Book Sections',
    body: 'Manage your Front Matter, Chapters, and End Matter from here. Use the + buttons to add sections and drag to reorder them.',
    icon: '📑',
    placement: 'right' as const,
  },
  {
    selector: '.notion-toolbar',
    title: 'Formatting Toolbar',
    body: 'Format headings, text styles, and alignment. Insert images, check grammar, save your work, and preview the PDF.',
    icon: '✏️',
    placement: 'below' as const,
  },
  {
    selector: '.notion-editor-content',
    title: 'Writing Area',
    body: 'Click here and start writing your book. The active section selected on the left will appear in this space.',
    icon: '📝',
    placement: 'center' as const,
  },
  {
    selector: '#editor-tips-btn',
    title: 'Quick Tips',
    body: 'Click this button anytime to see helpful tips — like how to paste a full document and split it into chapters, and what gets added to your book automatically.',
    icon: '💡',
    placement: 'left' as const,
  },
  {
    selector: null,
    title: "You're ready to write!",
    body: "Save often, use Preview to check your PDF layout, and click Continue when you're done with the content.",
    icon: '🚀',
    placement: 'center' as const,
  },
];

const PADDING = 10;

interface Rect { top: number; left: number; right: number; bottom: number; }

function getTooltipStyle(rect: Rect | null, placement: string): React.CSSProperties {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  if (!rect || placement === 'center') {
    return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }

  if (placement === 'right') {
    const tooltipTop = rect.top + (rect.bottom - rect.top) / 2 - 100;
    return {
      top: Math.max(16, Math.min(vh - 260, tooltipTop)),
      left: rect.right + 20,
    };
  }

  if (placement === 'below') {
    return {
      top: Math.min(rect.bottom + 16, vh - 260),
      left: Math.max(16, Math.min(vw - 356, rect.left + (rect.right - rect.left) / 2 - 170)),
    };
  }

  if (placement === 'left') {
    const tooltipTop = rect.top + (rect.bottom - rect.top) / 2 - 100;
    return {
      top: Math.max(16, Math.min(vh - 260, tooltipTop)),
      left: Math.max(16, rect.left - 340),
    };
  }

  return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
}

interface EditorTourProps {
  onComplete: () => void;
}

const EditorTour: React.FC<EditorTourProps> = ({ onComplete }) => {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);

  const currentStep = STEPS[step];

  // Lock all scrollable areas while tour is active
  useEffect(() => {
    const editorContent = document.querySelector('.notion-editor-content') as HTMLElement | null;
    const prevBodyOverflow = document.body.style.overflow;
    const prevEditorOverflow = editorContent?.style.overflow ?? '';

    document.body.style.overflow = 'hidden';
    if (editorContent) editorContent.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = prevBodyOverflow;
      if (editorContent) editorContent.style.overflow = prevEditorOverflow;
    };
  }, []);

  const measureTarget = useCallback(() => {
    if (!currentStep.selector) { setRect(null); return; }
    const el = document.querySelector(currentStep.selector);
    if (el) {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top - PADDING, left: r.left - PADDING, right: r.right + PADDING, bottom: r.bottom + PADDING });
    }
  }, [currentStep.selector]);

  useEffect(() => {
    measureTarget();
    window.addEventListener('resize', measureTarget);
    return () => window.removeEventListener('resize', measureTarget);
  }, [measureTarget]);

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setStep(s => s + 1);
    } else {
      onComplete();
    }
  };

  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const t = rect ? Math.max(0, rect.top) : 0;
  const l = rect ? Math.max(0, rect.left) : 0;
  const r = rect ? Math.min(vw, rect.right) : vw;
  const b = rect ? Math.min(vh, rect.bottom) : vh;

  const overlayColor = 'rgba(15, 15, 15, 0.55)';

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000 }}>
      {/* Spotlight: 4 dark panels around target */}
      {rect ? (
        <>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: t, background: overlayColor }} />
          <div style={{ position: 'absolute', top: t, left: 0, width: l, height: b - t, background: overlayColor }} />
          <div style={{ position: 'absolute', top: t, left: r, right: 0, height: b - t, background: overlayColor }} />
          <div style={{ position: 'absolute', top: b, left: 0, right: 0, bottom: 0, background: overlayColor }} />
          {/* Highlight border around target */}
          <div style={{
            position: 'absolute',
            top: t, left: l, width: r - l, height: b - t,
            border: '2px solid #f97316',
            borderRadius: 8,
            pointerEvents: 'none',
          }} />
        </>
      ) : (
        <div style={{ position: 'absolute', inset: 0, background: overlayColor }} />
      )}

      {/* Tooltip card */}
      <div style={{
        position: 'absolute',
        ...getTooltipStyle(rect, currentStep.placement),
        width: 320,
        background: '#ffffff',
        borderRadius: 14,
        padding: '22px 24px 18px',
        boxShadow: '0 12px 40px rgba(0,0,0,0.22)',
        zIndex: 1001,
        borderTop: '4px solid #f97316',
      }}>
        {/* Step header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <span style={{ fontSize: 22 }}>{currentStep.icon}</span>
          <span style={{ fontWeight: 600, fontSize: 15, color: '#111827' }}>
            {currentStep.title}
          </span>
        </div>

        <p style={{ fontSize: 13.5, color: '#4b5563', lineHeight: 1.65, margin: '0 0 18px' }}>
          {currentStep.body}
        </p>

        {/* Dot indicators */}
        <div style={{ display: 'flex', gap: 5, marginBottom: 16 }}>
          {STEPS.map((_, i) => (
            <div key={i} style={{
              width: i === step ? 18 : 6,
              height: 6,
              borderRadius: 3,
              background: i === step ? '#f97316' : '#e5e7eb',
              transition: 'width 0.2s',
            }} />
          ))}
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            onClick={onComplete}
            style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 13, cursor: 'pointer', padding: '6px 0' }}
          >
            Skip tour
          </button>
          <button
            onClick={handleNext}
            style={{
              background: '#f97316', color: '#fff', border: 'none',
              borderRadius: 7, padding: '8px 20px', fontSize: 13.5,
              fontWeight: 600, cursor: 'pointer',
            }}
          >
            {step < STEPS.length - 1 ? 'Next →' : 'Got it!'}
          </button>
        </div>
      </div>
    </div>
  );
};

export { TOUR_KEY };
export default EditorTour;
